import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { IStoreWithAuth, getUser } from '@crm-framework/auth';
import {
  GroupOptimizedMenuListWithDisabledHeader,
  GroupOptimizedMenuList,
  OptimizedMenuList,
  Option,
} from '@crm-framework/form-fields';
import { EMPTY_VALUE } from '@crm-framework/utils';

import { USER_RIGHTS } from '@crm-package/auth';
import {
  DICTIONARY_EMPTY_VALUE_ID,
  DictionarySelect,
  IDictionarySelect,
} from '@crm-package/dictionaries';

import { createOptionsMappers, managerOptionsSorting } from './optionsMappers';

export type ManagerSelectProps = IDictionarySelect & {
  leadBUId?: number;
  allowEmpty?: boolean;
  emptyByPermission?: boolean;
  disableSelectHeader?: boolean;
};

export const getEmptySelectOption = createSelector(
  getUser,
  (_: IStoreWithAuth, allowEmpty?: boolean) => allowEmpty,
  (_: IStoreWithAuth, _allowEmpty?: boolean, emptyByPermission?: boolean) =>
    emptyByPermission,
  (user, allowEmpty, emptyByPermission) => {
    let $allowEmpty = allowEmpty;

    if (!allowEmpty) return undefined;

    if (emptyByPermission) {
      $allowEmpty =
        user?.rights.includes(USER_RIGHTS.ALLOW_EMPTY_ASSIGNMENT) ||
        user?.rights.includes(USER_RIGHTS.ALL);
    }

    return {
      name: '',
      managers: [
        {
          id: DICTIONARY_EMPTY_VALUE_ID,
          name: EMPTY_VALUE,
          disabled: !$allowEmpty,
          businessUnits: [],
        },
      ],
    };
  }
);

export const ManagersSelect: React.FC<ManagerSelectProps> = React.memo(
  ({
    leadBUId,
    isGrouped,
    dictionaryName,
    allowEmpty,
    emptyByPermission,
    disableSelectHeader,
    isMulti,
    optionsSorting = managerOptionsSorting,
    ...props
  }) => {
    const user = useSelector(getUser);

    const emptyOptionsSelector = useCallback(
      (state: IStoreWithAuth) =>
        getEmptySelectOption(state, allowEmpty, emptyByPermission),
      [allowEmpty, emptyByPermission]
    );

    const emptyOptions = useSelector(emptyOptionsSelector);

    const MenuList = useMemo(() => {
      if (isGrouped && isMulti && disableSelectHeader)
        return GroupOptimizedMenuListWithDisabledHeader;

      return isGrouped ? GroupOptimizedMenuList : OptimizedMenuList;
    }, [isGrouped, disableSelectHeader, isMulti]);

    const components = useMemo(
      () => ({
        MenuList,
        Option,
      }),
      [MenuList]
    );

    const { optionsMapper, groupedOptionsMapper } = useMemo(
      () => createOptionsMappers(leadBUId, user?.id),
      [leadBUId]
    );

    return (
      <DictionarySelect
        key={`${dictionaryName}-${leadBUId}`}
        isGrouped={isGrouped}
        optionsMapper={optionsMapper}
        optionsSorting={optionsSorting}
        groupedOptionsMapper={groupedOptionsMapper}
        dictionaryName={dictionaryName}
        components={components}
        customEmptyValue={emptyOptions}
        isMulti={isMulti}
        {...props}
      />
    );
  }
);
